import React, { ReactElement, ReactNode } from 'react';
import { errorLogger } from '../../services/errorLogger';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    errorLogger.logError('React Component Error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    console.error('Error caught by boundary:', error, errorInfo);
  }

  render(): ReactElement {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <h2 className="error-boundary__title">Something went wrong</h2>

            <details className="error-boundary__details">
              <summary className="error-boundary__summary">Error Details</summary>
              <div className="error-boundary__content">
                <p className="error-boundary__message">{this.state.error?.toString()}</p>
                {this.state.errorInfo && (
                  <pre className="error-boundary__stack">{this.state.errorInfo.componentStack}</pre>
                )}
              </div>
            </details>

            <div className="error-boundary__actions">
              <button
                className="error-boundary__btn error-boundary__btn--primary"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
              <button
                className="error-boundary__btn error-boundary__btn--secondary"
                onClick={() => errorLogger.exportLogsAsFile()}
              >
                Export Logs
              </button>
              <button
                className="error-boundary__btn error-boundary__btn--secondary"
                onClick={() => {
                  const logs = errorLogger.getLogs();
                  console.table(logs);
                  alert(`${logs.length} logs printed to console`);
                }}
              >
                View Logs in Console
              </button>
              <button
                className="error-boundary__btn error-boundary__btn--secondary"
                onClick={async () => {
                  const result = await errorLogger.sendLogsToBackend({
                    includeAllLogs: true,
                  });
                  alert(result.message);
                }}
              >
                Send Logs to Developer
              </button>
            </div>

            <div className="error-boundary__info">
              <p className="error-boundary__timestamp">
                Time: {new Date().toLocaleString()}
              </p>
              <p className="error-boundary__url">URL: {window.location.href}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children as ReactElement;
  }
}
